// src/components/loginpage/Login.jsx
// Your logic and auth boxes unchanged. Structural changes marked CHANGED/NEW:
//  - left panel: RW Login Central / At your service / version plates removed,
//    Riftworks brand plate added at the top, statement plate moved to the bottom
//  - the Sign Up button has left the panel; each card face now carries its own
//    footer link, and the front face carries the Kuestion lockup
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './Login.css';
import Loginbox from './loginbox/Loginbox';
import Signupbox from './signupbox/Signupbox';
import Forgotbox from './forgotbox/Forgotbox';
import Notifier from './notifier/Notifier';
import { ReactComponent as LoginSvg } from './res/login-graphic.svg';
import Regsuccess from './signupbox/regsuccess/Regsuccess';
import VideoMask from '../landingpage/VideoMask';
import Noticeboard from '../noticeboard/Noticeboard';
import KuestionMark from '../kuestion/KuestionMark';
import { readOpenSitting } from '../kuestion/shared/openSitting';
import useTheme from '../../hooks/useTheme';
import logoMark from '../../assets/images/logo-mark.png';
import { kq, KUESTION_PUBLIC_ADDRESS } from '../../config/routes';

const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2.4M12 19.6V22M2 12h2.4M19.6 12H22M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M19.1 4.9l-1.7 1.7M6.6 17.4l-1.7 1.7" />
  </svg>
);
const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.5 14.5A8.6 8.6 0 0 1 9.5 3.5a8.6 8.6 0 1 0 11 11Z" />
  </svg>
);

// Must stay in sync with the .flip-card-inner transition in Login.css.
const FLIP_MS = 700;

// `area` decides which door this is (Separation contract v2.1 §5):
//   'kuestion' → the Kuestion sign-in under its mount point; serves Kuestion
//                and nothing else, and signs up with apptype "kuestion"
//   'suite'    → the apex Riftworks sign-in, unchanged in behaviour
const Login = ({ area = 'suite' }) => {
  const isKuestion = area === 'kuestion';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 'login' is the card front; 'signup' and 'forgot' both live on the back face.
  const [view, setView] = useState('login');
  // Which component the back face currently renders. Only ever changed while
  // the back face is hidden, so the swap is invisible.
  const [backView, setBackView] = useState('signup');
  const [signupCompleted, setSignupCompleted] = useState(false);
  const flipTimer = useRef(null);
  // Same :root[data-theme] contract as the website and the Kuestion app.
  const [theme, toggleTheme] = useTheme();
  // Reached from an interrupted sitting? Then this is not a generic sign-in
  // (build brief §15): the exam is still open, and signing in goes back to it.
  const [openSitting] = useState(() => (isKuestion ? readOpenSitting() : null));

  // single noticeboard feed + signal
  const [note, setNote] = useState({ msg: '', type: 'info', signal: 0 });
  const [sig, setSig] = useState(0);

  useEffect(() => () => clearTimeout(flipTimer.current), []);

  // Entry points from the reset flow (same flags in either area):
  //   login?reset=1  → password was just reset, show the confirmation
  //   login?forgot=1 → a dead reset link sent them back for a fresh one
  // The flag is stripped afterwards so a refresh doesn't replay it.
  useEffect(() => {
    const reset = searchParams.get('reset') === '1';
    const forgot = searchParams.get('forgot') === '1';
    if (!reset && !forgot) return;

    if (reset) {
      onNotify('Your password has been reset. Please log in.', 'success');
    } else {
      setBackView('forgot');
      setView('forgot');
    }
    // keep ?suite=1: without it /login hands over to the Kuestion door
    setSearchParams(searchParams.has('suite') ? { suite: '1' } : {}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Move to a view, flipping the card as needed. Going straight from one back
  // view to the other (signup → forgot) would swap content with no animation,
  // so we flip home first and then out again once the card has settled.
  const goTo = (next) => {
    clearTimeout(flipTimer.current);
    setSignupCompleted(false);

    if (next === 'login') {
      setView('login');
      return;
    }
    if (view !== 'login') {
      setView('login');
      flipTimer.current = setTimeout(() => {
        setBackView(next);
        setView(next);
      }, FLIP_MS);
      return;
    }
    setBackView(next);
    setView(next);
  };

  const handleToggle = () => goTo(view === 'login' ? 'signup' : 'login');

  // IMPORTANT: schedule to next tick to defeat React 18 batching
  const onNotify = (msg, type = 'info') => {
    setSig(s => {
      const next = s + 1;
      // next-tick schedule ensures each call is a distinct render
      setTimeout(() => setNote({ msg, type, signal: next }), 0);
      return next;
    });
  };

  // Each door opens its own area and never the other one.
  const handleLoginSuccess = () => {
    navigate(isKuestion ? kq() : '/portal');
  };

  // 🔥 Full-page Regsuccess mode: this completely replaces the login layout
  if (signupCompleted) {
    return (
      <Regsuccess
        onLogin={() => {
          setSignupCompleted(false);
          setView('login');
        }}
        onHome={() => {
          window.location.assign('/');
        }}
        onCountdownEnd={() => {
          setSignupCompleted(false);
          setView('login');
        }}
      />
    );
  }

  // Normal login / signup layout
  return (
    <div className="login-container">
      <div className="maincolleft">
        <VideoMask />{/* must stay the first child — the CSS sits it behind the panel */}

        {/* NEW — Riftworks brand plate, doubles as the way back to the landing page */}
        <Link to="/" className="rw-brand-plate" title="Back to Riftworks home">
          <img src={logoMark} alt="Riftworks" />
          <span>
            <strong>RIFTWORKS</strong>
            <em>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to home
            </em>
          </span>
        </Link>

        <div className="innerwrapper1">
          <LoginSvg className="login-svg" />
        </div>

        {/* moved to the bottom, in place of the old Sign Up button */}
        {isKuestion ? (
          <div className="login-statement">
            <span className="login-statement__kicker">Kuestion · Sign in</span>
            <h2>Kuestion has its own door.</h2>
            <p>
              This sign-in serves Kuestion and nothing else. Your account opens the mode it is
              set to — Examiner for teachers, Student for candidates.
            </p>
            <p className="login-statement__fine">Signing out here leaves the rest of Riftworks as it was.</p>
          </div>
        ) : (
          <div className="login-statement">
            <span className="login-statement__kicker">Riftworks · Sign in</span>
            <h2>Your Riftworks account.</h2>
            <p>
              This sign-in opens the Riftworks portal. Kuestion has its own door
              at <Link to={kq('login')}>{KUESTION_PUBLIC_ADDRESS}</Link>.
            </p>
          </div>
        )}
      </div>

      <div className="maincolright">
        <div className="login-tools">
        {/* Before anyone signs in: a maintenance notice about sign-up is read first. */}
        <Noticeboard surface={isKuestion ? 'kuestion' : 'suite'} store="rw" size={36} />
        <button
          type="button"
          className="login-theme"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
          aria-label={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
        </div>
        <div className="flip-card">
          <div className={`flip-card-inner${view !== 'login' ? ' flipped' : ''}`}>
            <div className="flip-card-front">
              {/* NEW — Kuestion lockup */}
              {isKuestion && (
                <div className="kq-lockup">
                  <span className="kq-lockup__tile">
                    <KuestionMark bodyFill="currentColor" leafFill="currentColor" glyphFill="var(--color-accent)" />
                  </span>
                  <span>
                    <strong>Kuestion</strong>
                    <em>Riftworks · Examination platform</em>
                  </span>
                </div>
              )}

              <div className="kq-facehead">
                <span className="kq-facehead__kicker">Sign in</span>
                <h1>{isKuestion ? 'Sign in to Kuestion' : 'Welcome back.'}</h1>
                {isKuestion && (
                  <p className="kq-facehead__sub">Teachers and candidates use the same door &mdash; at {KUESTION_PUBLIC_ADDRESS}.</p>
                )}
              </div>

              {openSitting && (
                <div className="kq-resume" role="status">
                  <span className="kq-resume__dot" aria-hidden="true" />
                  <span>
                    <strong>Your exam is still open</strong> &mdash; sign in to go back to it.
                    {openSitting.name ? <> Your answers to <em>{openSitting.name}</em> are saved on the server, and so is the clock.</> : <> Your answers are saved on the server, and so is the clock.</>}
                  </span>
                </div>
              )}

              <Loginbox
                area={area}
                onNotify={onNotify}
                onLoginSuccess={handleLoginSuccess}
                onForgotPassword={() => goTo('forgot')}
                resumeTo={openSitting ? kq(`student/exam/${openSitting.id}`) : null}
              />

              {/* NEW — the flip now lives here */}
              <div className="rw-card-footer">
                No account yet?{' '}
                <button type="button" onClick={handleToggle}>Sign up</button>
              </div>
            </div>

            <div className="flip-card-back">
              {isKuestion && (
                <div className="kq-lockup">
                  <span className="kq-lockup__tile">
                    <KuestionMark bodyFill="currentColor" leafFill="currentColor" glyphFill="var(--color-accent)" />
                  </span>
                  <span><strong>Kuestion</strong></span>
                </div>
              )}

              {backView !== 'forgot' && (
                <div className="kq-facehead">
                  <span className="kq-facehead__kicker">Create account</span>
                  <h1>{isKuestion ? 'Start with Kuestion.' : 'Start with Riftworks.'}</h1>
                </div>
              )}

              {backView === 'forgot' ? (
                <Forgotbox
                  onNotify={onNotify}
                  onBack={() => goTo('login')}
                />
              ) : (
                <Signupbox
                  apptype={isKuestion ? 'kuestion' : 'logincentral'}
                  onNotify={onNotify}
                  onSignupSuccess={() => setSignupCompleted(true)}
                  onForgotPassword={() => goTo('forgot')}
                />
              )}

              <div className="rw-card-footer">
                Already registered?{' '}
                <button type="button" onClick={() => goTo('login')}>Log in</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Single, always-mounted noticeboard */}
      <Notifier
        message={note.msg}
        type={note.type}
        signal={note.signal}
        durationMs={5000}
        onClose={() => setNote({ msg: '', type: 'info', signal: 0 })}
      />
    </div>
  );
};

export default Login;
